import React, { Component } from 'react'
import PropTypes from 'prop-types'
import sortBy from 'sort-by'
import { Table, Column, Menu, Dropdown, Icon, Spin } from './common/Ant'
import { Anchor, ActionAnchor } from './common/Anchor'
import Select, { Option } from './common/Select'
import InputContainer from '../containers/common/InputContainer'
import { toTitleCase } from '../utils/textFormatters'
import { hasPermission } from '../utils/hasPermission'
import { createQueryString, parseQueryParams } from '../utils/queryParams'
import {
  PRESSURE_MONITOR_STATUS,
  DEFAULT_PAGE,
  DEFAULT_PER_PAGE,
  VIEW_ALL,
  USER_PERMISSIONS,
} from '../constants'
import { configurePagination, escapeRegExp } from '../utils/helpers'

class AllPressureMonitors extends Component {
  constructor(props) {
    super(props)

    this.state = {
      sortedInfo: {},
      data: this.props.pressureMonitors,
      searchText: '',
      filtered: false,
      page: DEFAULT_PAGE,
      perPage: DEFAULT_PER_PAGE,
      status: undefined,
      siteSlug: undefined,
    }
  }

  static propTypes = {
    pressureMonitors: PropTypes.arrayOf(PropTypes.object).isRequired,
    windowWidth: PropTypes.number.isRequired,
    meta: PropTypes.object,
    getAllPressureMonitors: PropTypes.func,
    deletePressureMonitor: PropTypes.func,
    updateQueryParams: PropTypes.func,
    isPressureMonitorLoading: PropTypes.bool,
    sites: PropTypes.arrayOf(PropTypes.object),
  }

  componentWillReceiveProps(nextProps) {
    this.handleSearch()
  }

  componentDidMount() {
    const { page, perPage, status, siteSlug } = parseQueryParams(
      global.location
    )
    const { getAllPressureMonitors } = this.props
    const query = {
      page: page ? parseInt(page, 10) : DEFAULT_PAGE,
      perPage: perPage ? parseInt(perPage, 10) : DEFAULT_PER_PAGE,
      status,
      siteSlug,
    }
    this.setState(query)
    getAllPressureMonitors && getAllPressureMonitors(createQueryString(query))
  }

  handleChange = (pagination, filters, sorter) => {
    const { getAllPressureMonitors, updateQueryParams } = this.props
    const { page, perPage, status, siteSlug } = this.state

    if (page !== pagination.current) {
      const query = {
        page: pagination.current,
        perPage,
        status,
        siteSlug,
      }

      const qs = createQueryString(query)
      getAllPressureMonitors && getAllPressureMonitors(qs)

      updateQueryParams && updateQueryParams({ search: qs })
    }

    this.setState({
      page: pagination.current,
      sortedInfo: sorter,
      searchText: '',
    })
  }

  handleFilterChange = keyName => value => {
    const { getAllPressureMonitors, updateQueryParams } = this.props
    const { page, perPage, status, siteSlug } = this.state

    const newValue =
      value === undefined || (value.length && value.indexOf('all')) > -1
        ? undefined
        : value

    const query = Object.assign(
      {
        page,
        perPage,
        status,
        siteSlug,
      },
      { [keyName]: newValue }
    )

    this.setState({ [keyName]: newValue })

    // if we expect the number of results to change, we need to reset to the
    // first page so API does not return a 404
    if (keyName !== 'page' || keyName !== 'perPage') {
      query.page = DEFAULT_PAGE
      this.setState({ page: DEFAULT_PAGE })
    }

    const qs = createQueryString(query)
    getAllPressureMonitors(qs)

    updateQueryParams &&
      updateQueryParams({
        search: qs,
      })
  }

  handleInputChange = e => {
    this.setState({ searchText: e.currentTarget.value }, () =>
      this.handleSearch()
    )
  }

  handleSearch = () => {
    const { searchText } = this.state
    const regex = new RegExp(escapeRegExp(searchText), 'gi')

    this.setState((state, props) => {
      const data = props.pressureMonitors
        .map(record => {
          let match = null
          if (record.pillarId) {
            match = record.pillarId.match(regex)
          } else if (record.description) {
            match = record.description.match(regex)
          } else if (record.id) {
            match = record.id.toString().match(regex)
          }

          return match ? record : null
        })
        .filter(record => !!record)

      return {
        filtered: !!searchText,
        data,
      }
    })
  }

  render() {
    const {
      deletePressureMonitor,
      meta,
      windowWidth,
      isPressureMonitorLoading,
      sites,
    } = this.props
    const { data, sortedInfo, searchText, perPage, status, siteSlug } = this.state

    const isFixedColumn = windowWidth < 750

    const isFiltering = !!(
      (status && status.length) || (siteSlug && siteSlug.length)
    )

    return (
      <section className="mb5 AllPressureMonitors">
        {hasPermission(USER_PERMISSIONS.ADD_PRESSURE_MONITOR) && (
          <div>
            <Anchor
              to="/inventory/pressure-monitors/create"
              className="mb3"
              button
            >
              Create New
            </Anchor>
            <div className="dib red">
              {isFiltering && (
                <p className="pl3">
                  Found {meta.total} Pressure Monitors matching your
                  criteria
                </p>
              )}
            </div>
          </div>
        )}
        <div className="flex-l items-center-l">
          <Select
            className="w-third-l mr3-l"
            placeholder="Status"
            mode="multiple"
            input={{
              value: status,
              onChange: this.handleFilterChange('status'),
            }}
            filterable
          >
            {['all', ...Object.keys(PRESSURE_MONITOR_STATUS)].map(x => (
              <Option value={PRESSURE_MONITOR_STATUS[x] || x} key={x}>
                {toTitleCase(PRESSURE_MONITOR_STATUS[x]) || VIEW_ALL}
              </Option>
            ))}
          </Select>
          <Select
            className="w-third-l"
            placeholder="Site"
            mode="multiple"
            input={{
              value: siteSlug,
              onChange: this.handleFilterChange('siteSlug'),
            }}
            filterable
          >
            {[{ slug: 'all', name: VIEW_ALL }, ...(sites || [])].map(x => (
              <Option value={x.slug} key={x.slug}>
                {x.name}
              </Option>
            ))}
          </Select>
        </div>
        <div className="table-filter">
          <InputContainer
            input={{
              placeholder: 'Filter',
              value: searchText,
              onChange: this.handleInputChange,
            }}
          />
        </div>
        {isPressureMonitorLoading ? (
          <Spin size="large" className="w-100 center mt5" />
        ) : (
          <Table
            dataSource={data}
            onChange={this.handleChange}
            rowKey="id"
            scroll={{ x: 650 }}
            bordered
            pagination={configurePagination({
              perPage,
              onShowSizeChange: (page, perPage) =>
                this.handleFilterChange('perPage')(perPage),
              ...meta,
            })}
            tableLayout="auto"
          >
            <Column
              title="Pillar ID"
              dataIndex="pillarId"
              sorter={sortBy('pillarId')}
              sortOrder={sortedInfo.field === 'pillarId' && sortedInfo.order}
              width={120}
              render={(text, { id }) => (
                <Anchor to={`/inventory/pressure-monitors/${id}`}>
                  {text}
                </Anchor>
              )}
            />
            <Column
              title="Module ID"
              dataIndex="moduleId"
              sorter={sortBy('moduleId')}
              sortOrder={sortedInfo.field === 'moduleId' && sortedInfo.order}
              width={150}
              render={text => (text ? text : '--')}
            />
            <Column
              title="Site"
              dataIndex="siteName"
              sorter={sortBy('siteName')}
              sortOrder={sortedInfo.field === 'siteName' && sortedInfo.order}
              width={150}
              render={(text, { siteSlug }) =>
                siteSlug ? (
                  <Anchor to={`/sites/${siteSlug}`}>{text}</Anchor>
                ) : (
                  '--'
                )
              }
            />
            <Column
              title="Asset Location"
              dataIndex="assetLocationPillarId"
              sorter={sortBy('assetLocationPillarId')}
              sortOrder={
                sortedInfo.field === 'assetLocationPillarId' &&
                sortedInfo.order
              }
              width={150}
              render={text =>
                text ? (
                  <Anchor to={`/inventory/asset-locations/${text}`}>
                    {text}
                  </Anchor>
                ) : (
                  '--'
                )
              }
            />
            <Column
              title="Status"
              dataIndex="status"
              sorter={sortBy('status')}
              sortOrder={sortedInfo.field === 'status' && sortedInfo.order}
              width={100}
              render={text => (text ? toTitleCase(text) : '--')}
            />
            <Column
              title="Description"
              dataIndex="description"
              sorter={sortBy('description')}
              sortOrder={sortedInfo.field === 'description' && sortedInfo.order}
              width={200}
              render={text => (text ? text : '--')}
            />
            <Column
              title="Activate Threshold"
              dataIndex="activateThreshold"
              sorter={sortBy('activateThreshold')}
              sortOrder={
                sortedInfo.field === 'activateThreshold' && sortedInfo.order
              }
              width={130}
              render={text => (text || text === 0 ? text : '--')}
            />
            <Column
              title="Deactivate Threshold"
              dataIndex="deactivateThreshold"
              sorter={sortBy('deactivateThreshold')}
              sortOrder={
                sortedInfo.field === 'deactivateThreshold' && sortedInfo.order
              }
              width={130}
              render={text => (text || text === 0 ? text : '--')}
            />
            <Column
              title="Audio Alert"
              dataIndex="audioAlert"
              sorter={sortBy('audioAlert')}
              sortOrder={sortedInfo.field === 'audioAlert' && sortedInfo.order}
              width={110}
              render={text => (text ? 'Yes' : 'No')}
            />
            {hasPermission(USER_PERMISSIONS.EDIT_PRESSURE_MONITOR) && (
              <Column
                fixed={isFixedColumn ? 'right' : false}
                width={100}
                render={(text, record) => {
                  const menu = (
                    <Menu>
                      <Menu.Item key="edit-pressure-monitor">
                        <Anchor
                          to={`/inventory/pressure-monitors/${record.id}/edit`}
                        >
                          Edit
                        </Anchor>
                      </Menu.Item>
                      <Menu.Item key="delete-pressure-monitor">
                        <ActionAnchor
                          onClick={() => deletePressureMonitor(record.id)}
                        >
                          Delete
                        </ActionAnchor>
                      </Menu.Item>
                    </Menu>
                  )

                  return (
                    <Dropdown overlay={menu} trigger={['click']}>
                      <a className="ant-dropdown-link">
                        Actions <Icon type="down" />
                      </a>
                    </Dropdown>
                  )
                }}
              />
            )}
          </Table>
        )}
      </section>
    )
  }
}

export default AllPressureMonitors
